import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Check, RotateCcw, Package, Zap, Calendar } from 'lucide-react';
import { aiApi } from '../../../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../../../components/Modal';

interface AITemplate {
  id: string;
  icon: string;
  title: string;
  description: string;
  prompt: string;
}

interface AIPlan {
  wheel: {
    name: string;
    slug: string;
    instantDraw: boolean;
    drawDate: string | null;
    status: string;
    spinsPerWhatsapp: number;
    blockByIp: boolean;
    isShared: boolean;
  };
  products: Array<{
    name: string;
    stock: number;
    weight: number;
    is_active: boolean;
    is_no_prize: boolean;
    description: string;
  }>;
}

interface AIAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'input' | 'loading' | 'preview' | 'confirming' | 'done';

const AIAssistantModal: React.FC<AIAssistantModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<Step>('input');
  const [prompt, setPrompt] = useState('');
  const [templates, setTemplates] = useState<AITemplate[]>([]);
  const [plan, setPlan] = useState<AIPlan | null>(null);
  const [error, setError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Load templates on mount
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      setStep('input');
      setPrompt('');
      setPlan(null);
      setError('');
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await aiApi.getTemplates();
      setTemplates(response.templates);
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectTemplate = (template: AITemplate) => {
    setPrompt(template.prompt);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Digite um comando para a IA');
      return;
    }

    setStep('loading');
    setError('');

    try {
      const response = await aiApi.processPrompt(prompt);
      if (response.preview && response.plan) {
        setPlan(response.plan);
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.error || 'Erro ao processar o prompt. Tente novamente.');
      setStep('input');
      toast.error(err.error || 'Erro ao gerar com IA');
    }
  };

  const handleConfirm = async () => {
    if (!plan) return;

    setStep('confirming');
    setError('');

    try {
      await aiApi.processPrompt(prompt, true, plan);
      setStep('done');
      toast.success('Roda de prêmios criada com IA!');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.error || 'Erro ao criar a roda. Tente novamente.');
      setStep('preview');
      toast.error(err.error || 'Erro ao criar a roda');
    }
  };

  const handleRetry = () => {
    setStep('input');
    setPlan(null);
    setError('');
  };

  const totalWeight = plan?.products.reduce((sum, p) => sum + p.weight, 0) || 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assistente de IA" size="xl">
      <div className="space-y-4">
        {/* Step: Input */}
        {step === 'input' && (
          <>
            {/* Templates Grid */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-tinta mb-3">
                Escolha um exemplo ou escreva seu próprio comando
              </p>

              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-mostarda-dark" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`group relative rounded-lg border p-4 text-left transition-all duration-200 hover:shadow-sm ${
                        prompt === template.prompt
                          ? 'border-mostarda-dark bg-mostarda/15 shadow-sm'
                          : 'border-tinta/15 bg-white hover:border-mostarda/60 hover:bg-mostarda/10'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl flex-shrink-0">{template.icon}</span>
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-tinta group-hover:text-bordo transition-colors">
                            {template.title}
                          </h4>
                          <p className="text-xs text-tinta-soft mt-0.5 line-clamp-2 italic">
                            {template.description}
                          </p>
                        </div>
                      </div>
                      {prompt === template.prompt && (
                        <div className="absolute top-2 right-2">
                          <Check className="h-4 w-4 text-mostarda-dark" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Prompt Input */}
            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Crie uma roda de prêmios para uma pizzaria com descontos de 10%, 20%, uma pizza grátis, e 'não ganhou' com peso alto..."
                className="w-full h-28 bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleGenerate();
                  }
                }}
              />
              <div className="absolute bottom-3 right-3 flex items-center gap-2">
                <span className="text-xs text-tinta-soft/70 italic hidden sm:inline">
                  ⌘+Enter para enviar
                </span>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-bordo/8 border border-bordo/30 p-3 text-sm text-bordo italic">
                {error}
              </div>
            )}

            {/* Action button */}
            <div className="flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-tinta bg-mostarda hover:bg-mostarda-light border border-mostarda-dark/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Gerar com IA
              </button>
            </div>
          </>
        )}

        {/* Step: Loading */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-mostarda border border-mostarda-dark/30 flex items-center justify-center animate-pulse">
                <Sparkles className="h-8 w-8 text-tinta" />
              </div>
              <div className="absolute -inset-2 rounded-full border-2 border-mostarda/40 border-t-mostarda-dark animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="font-display text-lg font-bold text-tinta">
                A <span className="italic text-bordo">IA</span> está criando sua roda...
              </h3>
              <p className="text-sm text-tinta-soft italic mt-1">Analisando seu pedido e gerando os prêmios</p>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && plan && (
          <>
            <div className="rounded-lg bg-mostarda/15 border border-mostarda/40 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-mostarda border border-mostarda-dark/30 flex items-center justify-center">
                  {plan.wheel.instantDraw ? (
                    <Zap className="h-5 w-5 text-tinta" />
                  ) : (
                    <Calendar className="h-5 w-5 text-tinta" />
                  )}
                </div>
                <div>
                  <h3 className="font-display text-base font-bold text-tinta">{plan.wheel.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-tinta-soft mt-0.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border ${
                        plan.wheel.instantDraw
                          ? 'bg-musgo/15 text-musgo border-musgo/30'
                          : 'bg-bordo/8 text-bordo border-bordo/30'
                      }`}
                    >
                      {plan.wheel.instantDraw ? 'Instantâneo' : 'Agendado'}
                    </span>
                    <span className="text-tinta-soft/50">•</span>
                    <span className="italic">/{plan.wheel.slug}</span>
                  </div>
                </div>
              </div>

              {plan.wheel.drawDate && (
                <p className="text-xs text-bordo bg-bordo/8 border border-bordo/20 rounded-lg px-3 py-1.5 mb-1 italic">
                  Sorteio agendado para: {new Date(plan.wheel.drawDate).toLocaleString('pt-BR')}
                </p>
              )}
            </div>

            {/* Products Preview */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-4 w-4 text-tinta-soft" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-tinta">
                  {plan.products.length} Prêmios
                </h4>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {plan.products.map((product, index) => {
                  const probability = ((product.weight / totalWeight) * 100).toFixed(1);

                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 rounded-lg border p-3 ${
                        product.is_no_prize
                          ? 'bg-creme-light border-tinta/15'
                          : 'bg-white border-tinta/15'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          product.is_no_prize
                            ? 'bg-tinta/10 text-tinta-soft'
                            : 'bg-bordo/8 text-bordo border border-bordo/20'
                        }`}
                      >
                        {product.is_no_prize ? '✕' : index + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium truncate ${
                            product.is_no_prize ? 'text-tinta-soft' : 'text-tinta'
                          }`}
                        >
                          {product.name}
                        </p>
                        {product.description && (
                          <p className="text-xs text-tinta-soft/70 italic truncate">{product.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-tinta-soft flex-shrink-0">
                        <span title="Estoque">
                          📦 {product.is_no_prize ? '∞' : product.stock}
                        </span>
                        <span title="Probabilidade" className="font-bold text-bordo">
                          {probability}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-bordo/8 border border-bordo/30 p-3 text-sm text-bordo italic">
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-tinta/10">
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 hover:bg-creme-light hover:text-tinta transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Refazer
              </button>

              <button
                onClick={handleConfirm}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
              >
                <Check className="h-4 w-4" />
                Confirmar e Criar
              </button>
            </div>
          </>
        )}

        {/* Step: Confirming */}
        {step === 'confirming' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-bordo/10 border border-bordo/30 flex items-center justify-center animate-pulse">
                <Package className="h-8 w-8 text-bordo" />
              </div>
              <div className="absolute -inset-2 rounded-full border-2 border-bordo/30 border-t-bordo animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="font-display text-lg font-bold text-tinta">Criando produtos e roda...</h3>
              <p className="text-sm text-tinta-soft italic mt-1">Configurando tudo automaticamente</p>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-16 h-16 rounded-full bg-musgo/15 border border-musgo/30 flex items-center justify-center">
              <Check className="h-8 w-8 text-musgo" />
            </div>
            <div className="text-center">
              <h3 className="font-display text-lg font-bold text-tinta">
                Tudo <span className="italic text-bordo">pronto!</span>
              </h3>
              <p className="text-sm text-tinta-soft italic mt-1">
                Sua roda de prêmios foi criada com sucesso
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AIAssistantModal;
